import { CorePortErrors } from 'modules/Cube/domain/cube.domain.types'
import { useCallback } from 'react'
import invariant from 'tiny-invariant'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  type DashboardApi20240730,
  dashboard20240730Client
} from '@sequencehq/api/dashboard/v20240730'

export type v1ApiBillingSchedule =
  DashboardApi20240730.GetBillingSchedule.BillingSchedule
export type v1ApiBillingScheduleSettings =
  DashboardApi20240730.GetBillingScheduleSettings.BillingScheduleSettings

type Data = {
  billingSchedule: v1ApiBillingSchedule
  billingScheduleSettings: v1ApiBillingScheduleSettings
}

type UseLoadBillingSchedule = () => (
  billingScheduleId: string
) => Promise<{ data: Data | null; error: CorePortErrors | null }>

export const useLoadBillingSchedule: UseLoadBillingSchedule = () => {
  const load = useCallback(async (billingScheduleId: string) => {
    const [billingScheduleResponse, billingScheduleSettingsResponse] =
      await Promise.all([
        dashboard20240730Client.getBillingSchedule({
          id: billingScheduleId
        }),
        dashboard20240730Client.getBillingScheduleSettings({
          billingScheduleId
        })
      ])

    if (!billingScheduleResponse.data || billingScheduleResponse.error) {
      return {
        data: null,
        error:
          billingScheduleResponse.res.status === 404
            ? CorePortErrors.NotFound
            : CorePortErrors.Other
      }
    }

    invariant(
      billingScheduleSettingsResponse.data ||
        billingScheduleSettingsResponse.res.status === 404,
      'Billing schedule settings not found'
    )
    const defaultAutoChargeSetting =
      await dashboardv99990101Client.getStripeCollectionsSettings()

    const shouldAutoEnableStripePayments = Boolean(
      defaultAutoChargeSetting?.data?.autoEnableStripePayments
    )

    /**
     * If we encounter a 404 on loading the billing schedule settings,
     * create a new settings object for this schedule. This is a temporary
     * fix until we perform this logic on the service instead.
     */
    const billingScheduleSettings =
      billingScheduleSettingsResponse.data ||
      (
        await dashboard20240730Client.postBillingScheduleSettings({
          body: {
            billingScheduleId,
            paymentProvider: shouldAutoEnableStripePayments ? 'STRIPE' : 'NONE',
            autoCharge: !!defaultAutoChargeSetting.data?.enableAutomaticPayments
          }
        })
      )?.data

    invariant(billingScheduleSettings, 'Billing schedule settings not found')

    return {
      data: {
        billingSchedule: billingScheduleResponse.data,
        billingScheduleSettings
      },
      error: null
    }
  }, [])

  return load
}
